import { Canvas, FabricImage, Rect, Ellipse, Triangle, IText, Path } from 'fabric';
import JSZip from 'jszip';
import type { DesignNode, ImageNodeData, ShapeNodeData, TextNodeData } from '../types/node.types';
import { CurvedIText } from './CurvedIText';
import { getCurveVisualBounds, getAutoPathStartOffset, type CurvePreset } from './curveUtils';
import { loadGoogleFont, normalizeWeight } from '../lib/fonts';

export const exportToPNG = (canvas: Canvas): string => {
  return canvas.toDataURL({ format: 'png', quality: 1, multiplier: 1 });
};

export const exportToJPEG = (canvas: Canvas): string => {
  return canvas.toDataURL({ format: 'jpeg', quality: 0.9, multiplier: 1 });
};

export const exportToSVG = (canvas: Canvas): string => {
  return canvas.toSVG();
};

export const downloadImage = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadSVG = (svgString: string, filename: string) => {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  downloadImage(url, filename);
  URL.revokeObjectURL(url);
};

export const downloadJSON = (jsonString: string, filename: string) => {
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  downloadImage(url, filename);
  URL.revokeObjectURL(url);
};

// ── Print export ────────────────────────────────────────────────────────────

const renderNodeOffscreen = async (canvas: Canvas, node: DesignNode): Promise<void> => {
  if (!node.visible) return Promise.resolve();

  if (node.type === 'image') {
    const data = node.data as ImageNodeData;
    return new Promise<void>((resolve) => {
      const htmlImg = new Image();
      htmlImg.crossOrigin = 'anonymous';
      htmlImg.onload = () => {
        const fImg = new FabricImage(htmlImg);
        const naturalW = fImg.width || node.size.width;
        const naturalH = fImg.height || node.size.height;
        const scale = Math.min(node.size.width / naturalW, node.size.height / naturalH);
        fImg.set({
          left: node.position.x,
          top: node.position.y,
          angle: node.rotation,
          opacity: node.opacity,
          scaleX: scale,
          scaleY: scale,
        });
        canvas.add(fImg);
        resolve();
      };
      htmlImg.onerror = () => {
        console.warn('[exportUtils] Failed to load image for export:', data.src);
        resolve();
      };
      htmlImg.src = data.src;
    });
  }

  if (node.type === 'shape') {
    const data = node.data as ShapeNodeData;
    const common = {
      left: node.position.x,
      top: node.position.y,
      angle: node.rotation,
      opacity: node.opacity,
      fill: data.fill,
      stroke: data.stroke,
      strokeWidth: data.strokeWidth ?? 0,
    };
    if (data.shapeType === 'circle') {
      canvas.add(new Ellipse({ ...common, rx: node.size.width / 2, ry: node.size.height / 2 }));
    } else if (data.shapeType === 'triangle') {
      canvas.add(new Triangle({ ...common, width: node.size.width, height: node.size.height }));
    } else {
      canvas.add(new Rect({ ...common, width: node.size.width, height: node.size.height }));
    }
    return Promise.resolve();
  }

  if (node.type === 'text') {
    const data = node.data as TextNodeData;
    const weight = normalizeWeight(data.fontWeight);
    await loadGoogleFont(data.fontFamily, weight);

    const displayText = data.uppercase
      ? data.text.toUpperCase()
      : data.lowercase
        ? data.text.toLowerCase()
        : data.text;

    const commonProps = {
      left: node.position.x,
      top: node.position.y,
      angle: node.rotation,
      opacity: node.opacity,
      fontSize: data.fontSize,
      fontFamily: data.fontFamily,
      fontWeight: data.fontWeight ?? 400,
      fontStyle: data.fontStyle ?? 'normal',
      fill: data.fill,
      textAlign: data.textAlign ?? 'left',
      underline: data.underline ?? false,
      charSpacing: data.charSpacing ?? 0,
      lineHeight: data.lineHeight ?? 1.16,
    };

    if (data.curvePathData) {
      const curvePath = new Path(data.curvePathData, {
        visible: false,
        selectable: false,
        evented: false,
      });
      const pathSide = 'left';
      // Mirror the canvas auto-centering logic: if no explicit offset was set,
      // compute one so text is centered along the curve path.
      const autoOffset = data.pathStartOffset != null && data.pathStartOffset !== 0
        ? data.pathStartOffset
        : getAutoPathStartOffset(
            (data.curvePreset ?? 'none') as CurvePreset,
            displayText,
            commonProps.fontSize ?? 40,
            commonProps.fontFamily ?? 'Arial',
            commonProps.fontWeight ?? 400,
            commonProps.fontStyle ?? 'normal',
            commonProps.charSpacing ?? 0,
            node.size.width,
            data.curveAmount ?? 50,
            data.curveRadius,
          );
      const textObj = new CurvedIText(displayText, {
        ...commonProps,
        path: curvePath,
        pathStartOffset: autoOffset,
        pathSide,
        pathAlign: 'center',
        objectCaching: false,
      });
      textObj.initDimensions();
      const fs = commonProps.fontSize ?? 40;
      const textW = textObj.width || node.size.width;
      const bounds = getCurveVisualBounds(
        (data.curvePreset ?? 'none') as CurvePreset,
        textW, fs, data.curveAmount ?? 50, data.curveRadius,
      );
      const padX = Math.max(0, (bounds.width - textObj.width) / 2);
      const padY = Math.max(0, (bounds.height - textObj.height) / 2);
      textObj.set({ padding: Math.ceil(Math.max(padX, padY)) });
      canvas.add(textObj);
    } else {
      canvas.add(new IText(displayText, commonProps));
    }

    return Promise.resolve();
  }

  return Promise.resolve();
};

// ── Surface image export (for mockup generation) ─────────────────────────────

const MOCKUP_MULTIPLIER = 2; // Good quality for SudoMock without being excessively large

/**
 * Export each populated surface as a PNG Blob keyed by surface name.
 * Used during save to persist per-surface design images for mockup generation.
 */
export const exportSurfaceImages = async (
  surfaces: Record<string, DesignNode[]>,
  dimensions: Record<string, { width: number; height: number }>,
): Promise<Record<string, Blob>> => {
  const results: Record<string, Blob> = {};

  const populated = Object.entries(surfaces).filter(([, nodes]) => nodes.length > 0);
  if (populated.length === 0) return results;

  await Promise.all(
    populated.map(async ([surface, nodes]) => {
      const dims = dimensions[surface];
      if (!dims) return;

      const el = document.createElement('canvas');
      el.width = dims.width;
      el.height = dims.height;
      const canvas = new Canvas(el, { width: dims.width, height: dims.height });

      const sorted = [...nodes].sort((a, b) => a.zIndex - b.zIndex);
      for (const node of sorted) {
        await renderNodeOffscreen(canvas, node);
      }
      canvas.renderAll();

      const dataUrl = canvas.toDataURL({ format: 'png', quality: 1, multiplier: MOCKUP_MULTIPLIER });
      canvas.dispose();

      // Convert data URL to Blob
      const response = await fetch(dataUrl);
      results[surface] = await response.blob();
    }),
  );

  return results;
};

// Print exports are rendered at a higher multiplier so the output is
// large enough for physical printing (300 DPI equivalent).
const PRINT_MULTIPLIER = 4;

export const exportAllSurfacesAsZip = async (
  surfaces: Record<string, DesignNode[]>,
  dimensions: Record<string, { width: number; height: number }>
): Promise<void> => {
  const populated = Object.entries(surfaces).filter(([, nodes]) => nodes.length > 0);
  if (populated.length === 0) {
    throw new Error('No design elements found. Add some graphics before exporting.');
  }

  const zip = new JSZip();

  await Promise.all(
    populated.map(async ([surface, nodes]) => {
      const dims = dimensions[surface];
      if (!dims) return;

      const el = document.createElement('canvas');
      el.width = dims.width;
      el.height = dims.height;
      const canvas = new Canvas(el, {
        width: dims.width,
        height: dims.height,
      });

      const sorted = [...nodes].sort((a, b) => a.zIndex - b.zIndex);
      for (const node of sorted) {
        await renderNodeOffscreen(canvas, node);
      }
      canvas.renderAll();

      const dataUrl = canvas.toDataURL({ format: 'png', quality: 1, multiplier: PRINT_MULTIPLIER });
      canvas.dispose();

      const base64 = dataUrl.split(',')[1];
      zip.file(`${surface}.png`, base64, { base64: true });
    })
  );

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'print-assets.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
